import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, SA_ORG_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Building2, Plus, Power, LogIn, Users, KeyRound, MailCheck, FileText, Receipt, FileSignature, ShieldAlert } from "lucide-react";
import MigrationPanel from "@/components/MigrationPanel";
import SaasInvoiceDialog from "@/components/SaasInvoiceDialog";
import AgreementDialog from "@/components/AgreementDialog";
import SuperAdminLoginActivity from "@/components/SuperAdminLoginActivity";
import { INDUSTRIES, industryLabel } from "@/lib/industry";

const EMPTY = { name: "", plan: "standard", industry: "concrete", timezone: "Australia/Hobart", brand_primary: "#F57C00", admin_email: "", admin_name: "", admin_password: "", plant_name: "", send_welcome_email: true };

function CreateOrgDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/orgs", f);
      const w = data.welcome_email;
      toast.success(`Organization ${data.name} created${w ? (w.status === "sent" ? ` · welcome email sent to ${w.to}` : ` · welcome email FAILED (${w.error || "check email settings"})`) : ""}`);
      setF(EMPTY); onCreated(data); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-surface border-industrial" data-testid="create-org-dialog">
        <DialogHeader><DialogTitle className="heading uppercase font-black">New Organization</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="label-tag">Organization name</Label><Input data-testid="org-form-name" value={f.name} onChange={set("name")} className="rounded-none bg-transparent border-industrial" /></div>
          <div className="col-span-2"><Label className="label-tag">Industry</Label>
            <select data-testid="org-form-industry" value={f.industry} onChange={set("industry")} className="w-full rounded-none bg-surface border border-industrial px-3 py-2 text-sm">
              {INDUSTRIES.map((i) => <option key={i.value} value={i.value} className="bg-surface">{i.label}</option>)}
            </select>
            <div className="mono text-[10px] text-muted-industrial mt-1">Concrete = full batching suite. Mulch/Freight/Other = lean Fleet Dispatch (dispatch, trucks, GPS, jobs, contractors).</div>
          </div>
          <div><Label className="label-tag">Plan</Label><Input data-testid="org-form-plan" value={f.plan} onChange={set("plan")} className="rounded-none bg-transparent border-industrial" /></div>
          <div><Label className="label-tag">Timezone</Label><Input data-testid="org-form-tz" value={f.timezone} onChange={set("timezone")} className="rounded-none bg-transparent border-industrial" /></div>
          <div><Label className="label-tag">Brand colour</Label><Input data-testid="org-form-color" value={f.brand_primary} onChange={set("brand_primary")} className="rounded-none bg-transparent border-industrial mono" /></div>
          <div><Label className="label-tag">First plant name</Label><Input data-testid="org-form-plant" value={f.plant_name} onChange={set("plant_name")} className="rounded-none bg-transparent border-industrial" /></div>
          <div className="col-span-2 border-t border-industrial pt-3 label-tag">First administrator</div>
          <div><Label className="label-tag">Admin name</Label><Input data-testid="org-form-admin-name" value={f.admin_name} onChange={set("admin_name")} className="rounded-none bg-transparent border-industrial" /></div>
          <div><Label className="label-tag">Admin email</Label><Input data-testid="org-form-admin-email" value={f.admin_email} onChange={set("admin_email")} className="rounded-none bg-transparent border-industrial" /></div>
          <div className="col-span-2"><Label className="label-tag">Admin password (min 8)</Label><Input data-testid="org-form-admin-password" type="text" value={f.admin_password} onChange={set("admin_password")} className="rounded-none bg-transparent border-industrial mono" /></div>
          <label className="col-span-2 flex items-center gap-2 cursor-pointer border border-industrial px-3 py-2" data-testid="org-form-welcome-label">
            <input type="checkbox" data-testid="org-form-welcome-cb" checked={f.send_welcome_email} onChange={(e) => setF({ ...f, send_welcome_email: e.target.checked })} />
            <span className="text-xs"><b>Send branded welcome email</b> — login link, credentials and the 5-minute setup checklist go to the admin the moment the org is created.</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button data-testid="org-form-submit" className="rounded-none bg-primary-orange text-white" disabled={busy} onClick={submit}>{busy ? "Creating…" : "Create organization"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrgUsersDialog({ org, onClose }) {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ email: "", name: "", password: "", role: "dispatcher" });
  const load = () => api.get(`/superadmin/orgs/${org.id}/users`).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, [org.id]); // eslint-disable-line
  const add = async () => {
    try { await api.post(`/superadmin/orgs/${org.id}/users`, f); setF({ email: "", name: "", password: "", role: "dispatcher" }); load(); toast.success("User added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reset = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 8 chars)`);
    if (!pw) return;
    try { await api.post(`/superadmin/users/${u.id}/reset-password`, { password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-surface border-industrial" data-testid="org-users-dialog">
        <DialogHeader><DialogTitle className="heading uppercase font-black">{org.name} · Users</DialogTitle></DialogHeader>
        <table className="w-full text-sm">
          <thead><tr className="label-tag text-left"><th className="py-1">Name</th><th>Email</th><th>Role</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-industrial/60" data-testid={`org-user-row-${u.email}`}>
                <td className="py-1.5">{u.name}</td><td className="mono text-xs">{u.email}</td><td className="mono text-[10px] uppercase">{u.role}{u.is_super_admin ? " · SA" : ""}</td>
                <td className="text-right"><button type="button" data-testid={`org-user-reset-${u.email}`} className="text-muted-industrial hover:text-primary-orange" onClick={() => reset(u)} title="Reset password"><KeyRound className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="grid grid-cols-4 gap-2 border-t border-industrial pt-3">
          <Input data-testid="org-user-name" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none bg-transparent border-industrial text-xs" />
          <Input data-testid="org-user-email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-none bg-transparent border-industrial text-xs" />
          <Input data-testid="org-user-password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="rounded-none bg-transparent border-industrial text-xs mono" />
          <div className="flex gap-1">
            <select data-testid="org-user-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="bg-transparent border border-industrial text-xs px-1 flex-1">
              {["admin", "plant_manager", "dispatcher", "batcher", "driver", "accounts"].map((r) => <option key={r} value={r} className="bg-surface">{r}</option>)}
            </select>
            <Button data-testid="org-user-add" size="sm" className="rounded-none bg-primary-orange text-white" onClick={add}><Plus className="w-4 h-4" /></Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PendingSignups({ onApproved }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/superadmin/signups?status=pending").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (sid, kind) => {
    if (kind === "reject" && !window.confirm("Reject this application and email them?")) return;
    setBusy(sid);
    try {
      if (kind === "approve") {
        const { data } = await api.post(`/superadmin/signups/${sid}/approve`);
        toast.success(`${data.name} approved — trial running, welcome email sent`);
        onApproved?.();
      } else {
        await api.post(`/superadmin/signups/${sid}/reject`, { notify: true });
        toast.success("Application rejected");
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!rows.length) return null;
  return (
    <div className="border border-primary-orange bg-surface p-4 space-y-3" data-testid="pending-signups">
      <div className="flex items-center gap-2">
        <div className="label-tag !text-primary-orange">Pending signups</div>
        <span className="mono text-[10px] uppercase tracking-widest text-primary-orange">{rows.length} waiting</span>
      </div>
      {rows.map((r) => (
        <div key={r.id} className="border border-industrial p-3" data-testid={`signup-row-${r.id}`}>
          <div className="flex items-start gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="heading font-black uppercase truncate">{r.company}</div>
              <div className="mono text-[11px] text-muted-industrial">{r.name} · {r.email} · {r.phone}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial mt-0.5">
                {r.trucks} trucks · ${r.trucks * 100}/mo · {r.country || "—"} · {new Date(r.created_at).toLocaleString()}
              </div>
              {r.notes ? <div className="text-sm text-muted-industrial mt-1.5 border-l-2 border-primary-orange pl-2">{r.notes}</div> : null}
            </div>
            <div className="flex gap-2 ml-auto">
              <Button data-testid={`signup-approve-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "approve")}
                className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white mono text-[10px] uppercase tracking-widest">Approve · 14d trial</Button>
              <Button data-testid={`signup-reject-${r.id}`} disabled={busy === r.id} onClick={() => act(r.id, "reject")}
                variant="outline" className="rounded-none border-industrial mono text-[10px] uppercase tracking-widest">Reject</Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Organizations() {
  const { user, refreshOrg } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [create, setCreate] = useState(false);
  const [usersFor, setUsersFor] = useState(null);
  const [invoicesFor, setInvoicesFor] = useState(null);
  const [agreementFor, setAgreementFor] = useState(null);
  const [confirmEnter, setConfirmEnter] = useState(null);
  const [entering, setEntering] = useState(false);
  const active = localStorage.getItem(SA_ORG_KEY) || user?.org_id;

  const load = () => api.get("/superadmin/orgs").then((r) => setOrgs(r.data)).catch(() => toast.error("Super-admin access required"));
  useEffect(() => { load(); }, []);

  const toggleActive = async (o) => {
    await api.patch(`/superadmin/orgs/${o.id}`, { active: !o.active });
    load();
  };
  const switchIndustry = async (o, industry) => {
    await api.patch(`/superadmin/orgs/${o.id}`, { industry });
    toast.success(`${o.name} → ${industryLabel(industry)} mode`);
    load();
  };
  const resendWelcome = async (o) => {
    try {
      const { data } = await api.post(`/superadmin/orgs/${o.id}/welcome-email`, {});
      if (data.status === "sent") toast.success(`Welcome email re-sent to ${data.to}`);
      else toast.error(`Welcome email failed: ${data.error || "check email settings"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const enter = async (o) => {
    // Returning to the super-admin's own home org needs no consent/audit.
    if (o.id === user.org_id) {
      localStorage.removeItem(SA_ORG_KEY);
      await refreshOrg();
      toast.success(`Back in your own organization`);
      window.location.assign("/");
      return;
    }
    // Entering another tenant's portal → require explicit consent + audit log.
    setConfirmEnter(o);
  };
  const doEnter = async () => {
    const o = confirmEnter;
    if (!o) return;
    setEntering(true);
    try {
      const { data } = await api.post(`/superadmin/orgs/${o.id}/access-log`, {});
      localStorage.setItem(SA_ORG_KEY, o.id);
      await refreshOrg();
      toast.success(`Now operating inside ${o.name}${data?.emailed ? " · access logged & emailed" : " · access logged"}`);
      window.location.assign("/");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not log access");
      setEntering(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="organizations-page">
      <div className="flex items-end justify-between">
        <div><div className="label-tag">ARMORBATCH GLOBAL · SUPER-ADMIN</div><h1 className="heading text-3xl font-black uppercase">Organizations</h1></div>
        <Button data-testid="create-org-btn" className="rounded-none bg-primary-orange text-white" onClick={() => setCreate(true)}><Plus className="w-4 h-4 mr-2" />New organization</Button>
      </div>
      <PendingSignups onApproved={load} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {orgs.map((o) => (
          <div key={o.id} data-testid={`org-card-${o.slug}`} className={`border bg-surface p-4 space-y-3 ${o.id === active ? "border-primary-orange" : "border-industrial"} ${!o.active ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ background: o.brand_primary }} />
                <div><div className="font-black heading uppercase">{o.name}</div><div className="mono text-[10px] text-muted-industrial">{o.slug} · {o.plan} · {o.timezone}</div>{o.trial_ends_at ? <div className="mono text-[10px] uppercase tracking-widest text-primary-orange">trial ends {new Date(o.trial_ends_at).toLocaleDateString()}</div> : null}
                  <select data-testid={`org-industry-${o.slug}`} value={o.industry || "concrete"} onChange={(e) => switchIndustry(o, e.target.value)} onClick={(e) => e.stopPropagation()}
                    className={`mt-1 rounded-none bg-surface border px-1.5 py-0.5 mono text-[9px] uppercase tracking-widest ${(o.industry || "concrete") === "concrete" ? "border-industrial text-muted-industrial" : "border-sky-500 text-sky-400"}`}>
                    {INDUSTRIES.map((i) => <option key={i.value} value={i.value} className="bg-surface">{i.value === "concrete" ? "Concrete mode" : `${i.label} · Fleet Dispatch`}</option>)}
                  </select>
                </div>
              </div>
              <span className={`mono text-[10px] uppercase px-1.5 py-0.5 border ${o.active ? "border-emerald-500 text-emerald-500" : "border-red-500 text-red-500"}`}>{o.active ? "active" : "suspended"}</span>
            </div>
            <div className="grid grid-cols-5 gap-1 text-center">
              {Object.entries(o.counts || {}).map(([k, v]) => (
                <div key={k} className="border border-industrial/60 py-1"><div className="font-bold text-sm">{v}</div><div className="mono text-[9px] uppercase text-muted-industrial">{k}</div></div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-industrial/60 pt-2">
              <span data-testid={`org-activation-${o.slug}`}
                title={o.activation?.admin_last_login_at
                  ? `${o.activation.admin_last_login_by} last signed in ${new Date(o.activation.admin_last_login_at).toLocaleString()}`
                  : "Nobody from this organization has logged in yet"}
                className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
                  o.activation?.signed_in ? "border-emerald-500 text-emerald-500" : "border-amber-500 text-amber-500"
                }`}>
                {o.activation?.signed_in ? "active · signed in" : "never signed in"}
              </span>
              <span className="mono text-[9px] text-muted-industrial text-right">
                {o.activation?.admin_last_login_at
                  ? `admin last in ${new Date(o.activation.admin_last_login_at).toLocaleDateString()} · ${o.activation.users_signed_in} user${o.activation.users_signed_in === 1 ? "" : "s"} active`
                  : o.welcome_email?.at ? `welcome sent ${new Date(o.welcome_email.at).toLocaleDateString()}` : "no welcome email sent"}
              </span>
            </div>
            <div className="flex gap-1.5">
              <Button data-testid={`org-enter-${o.slug}`} size="sm" variant="outline" className="rounded-none text-xs flex-1" onClick={() => enter(o)}><LogIn className="w-3.5 h-3.5 mr-1" />{o.id === active ? "Current" : "Enter"}</Button>
              <Button data-testid={`org-users-${o.slug}`} size="sm" variant="outline" className="rounded-none text-xs" onClick={() => setUsersFor(o)}><Users className="w-3.5 h-3.5" /></Button>
              <Button data-testid={`org-invoices-${o.slug}`} size="sm" variant="outline" className="rounded-none text-xs" title="Create / edit subscription invoices for this organization" onClick={() => setInvoicesFor(o)}><Receipt className="w-3.5 h-3.5" /></Button>
              <Button data-testid={`org-agreement-${o.slug}`} size="sm" variant="outline"
                className={`rounded-none text-xs ${o.agreement?.status === "signed" ? "text-emerald-500" : o.agreement?.status === "sent" ? "text-amber-400" : ""}`}
                title={o.agreement?.status === "signed" ? "Agreement signed — view / download" : o.agreement?.status === "sent" ? "Agreement sent — awaiting signature" : "Send the Software Licence Agreement to sign"}
                onClick={() => setAgreementFor(o)}><FileSignature className="w-3.5 h-3.5" /></Button>
              <Button data-testid={`org-welcome-${o.slug}`} size="sm" variant="outline" className={`rounded-none text-xs ${o.welcome_email?.status === "sent" ? "text-emerald-500" : o.welcome_email?.status === "failed" ? "text-red-400" : ""}`}
                title={o.welcome_email ? `Welcome email ${o.welcome_email.status} · ${o.welcome_email.to} · ${new Date(o.welcome_email.at).toLocaleString()}${o.welcome_email.error ? " · " + o.welcome_email.error : ""} — click to resend` : "Send branded welcome email to the org admin"}
                onClick={() => resendWelcome(o)}><MailCheck className="w-3.5 h-3.5" /></Button>
              <Button data-testid={`org-pack-${o.slug}`} size="sm" variant="outline" className="rounded-none text-xs"
                title="Download the ArmorBatch Global getting-started PDF for this organization"
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/welcome.pdf?plant=${encodeURIComponent(o.name)}`, "_blank")}><FileText className="w-3.5 h-3.5" /></Button>
              {o.id !== "org_gadtech" && <Button data-testid={`org-toggle-${o.slug}`} size="sm" variant="ghost" className={`rounded-none text-xs ${o.active ? "text-red-400" : "text-emerald-400"}`} onClick={() => toggleActive(o)}><Power className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>
        ))}
        {!orgs.length && <div className="text-muted-industrial flex items-center gap-2"><Building2 className="w-4 h-4" />No organizations yet.</div>}
      </div>
      <MigrationPanel />
      <div className="card-industrial p-5" data-testid="super-admin-activity-card">
        <div className="label-tag mb-4">Login Activity · all organisations</div>
        <SuperAdminLoginActivity orgs={orgs} />
      </div>
      <CreateOrgDialog open={create} onClose={() => setCreate(false)} onCreated={() => load()} />
      {usersFor && <OrgUsersDialog org={usersFor} onClose={() => setUsersFor(null)} />}
      <SaasInvoiceDialog org={invoicesFor} open={!!invoicesFor} onOpenChange={(v) => { if (!v) setInvoicesFor(null); }} />
      <AgreementDialog org={agreementFor} open={!!agreementFor} onOpenChange={(v) => { if (!v) setAgreementFor(null); }} onChanged={() => load()} />
      <Dialog open={!!confirmEnter} onOpenChange={(v) => { if (!v && !entering) setConfirmEnter(null); }}>
        <DialogContent className="max-w-md bg-surface border-industrial" data-testid="sa-enter-confirm-dialog">
          <DialogHeader><DialogTitle className="heading uppercase font-black flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-primary-orange" />Allow super-admin access?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-muted-industrial">
            <DialogDescription className="text-sm text-muted-industrial">You are about to enter <b className="text-ink">{confirmEnter?.name}</b>'s portal as a super-admin. You'll be able to see and act on their live data.</DialogDescription>
            <p className="mono text-[11px] bg-surface-2 border border-industrial/60 p-2">This access will be recorded in the audit log and an email notification sent to the ArmorBatch Global team.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" disabled={entering} onClick={() => setConfirmEnter(null)} data-testid="sa-enter-cancel">Cancel</Button>
            <Button className="rounded-none bg-primary-orange text-white" disabled={entering} onClick={doEnter} data-testid="sa-enter-confirm">{entering ? "Logging access…" : "Allow & enter"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
